import { createElement, type ReactNode } from 'react';

/**
 * Cleanlist's drawings, on a 48 by 48 grid. Each shape is
 * [tag, attributes, colour, kind]: the colour is a hex, 'currentColor', or
 * [CSS variable, fallback hex] (the variable flips dark parts in dark mode);
 * kind 'line' and 'thick' are round strokes, otherwise the shape is filled.
 */
export type CandyColour = string | [string, string];
export type CandyShape = [
  string,
  Record<string, string | number>,
  CandyColour,
  ('line' | 'thick')?,
];
export type CandyArt = Record<string, CandyShape[]>;

const camel = (key: string) =>
  key.replace(/-([a-z])/g, (_, c: string) => c.toUpperCase());
const paint = (colour: CandyColour) =>
  Array.isArray(colour) ? `var(${colour[0]}, ${colour[1]})` : colour;

/** Candy's round line widths on the 48 grid. */
const LINE = { line: 2.4, thick: 4.4 };

/** A drawing's shapes as SVG elements, in their colours. */
export function drawCandy(shapes: CandyShape[]): ReactNode[] {
  return shapes.map(([tag, attrs, colour, kind], index) => {
    const props: Record<string, unknown> = { key: index };
    for (const [key, value] of Object.entries(attrs)) props[camel(key)] = value;
    if (kind === 'line' || kind === 'thick') {
      Object.assign(props, {
        fill: 'none',
        stroke: paint(colour),
        strokeWidth: LINE[kind],
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
      });
    } else {
      props.fill = paint(colour);
    }
    return createElement(tag, props);
  });
}
